
// All navigation is handled from this file except;
// - HitManager which contains different views which are handled in HitManagerPage
// - HitChat which contains different views which are handled in HitChatPage

import React, { useEffect, Suspense } from "react";
import PropTypes from "prop-types";

// Routes
import { BrowserRouter, Routes, Route } from "react-router-dom"
import { RouteLoggedIn } from 'components/RouteLoggedIn/RouteLoggedIn.js'

// Loading
import LoadingScreen from "views/Loading/LoadingScreen.js";

// i18n
import 'i18n/i18n.js'

// Redux
import { useSelector, useDispatch } from 'react-redux';

import * as Metadata from './actions/Metadata';
import * as Notifications from './actions/Notifications';
import * as AppSettings from './actions/AppSettings';
import * as HitClub from './actions/HitClub';

import { store } from 'reducers/createStore';

import { useInterval } from 'utils/hooks.js';

// Translation
import { useTranslation } from 'react-i18next'

import { getDefaultDataFromBacked } from "utils/helpers.js"

// Pages (lazy loading)
const HitChatPage = React.lazy(() => import("views/HitChat/Pages/HitChatPage.js"))
const HitManagerPage = React.lazy(() => import("views/HitManager/Pages/HitManagerPage.js"))
const HitSearchPage = React.lazy(() => import("views/HitSearch/HitSearchPage.js"))
const StudentProfilePage = React.lazy(() => import("views/StudentProfile/StudentProfilePage.js"))
const HitBoardPage = React.lazy(() => import("views/StudentProfile/HitBoardPage.js"))
const HitFactorCollectionPage = React.lazy(() => import("views/CompanyProfile/HitFactorCollectionPage.js"))
const CompanyProfilePage = React.lazy(() => import("views/CompanyProfile/CompanyProfilePage.js"))
const Authenticate = React.lazy(() => import("views/Authenticate/Authenticate.js"))
const LoginPage = React.lazy(() => import("views/LoginPage/LoginPage.js"))
const SettingsPage = React.lazy(() => import("views/Settings/SettingsPage.js"))
const FaqPage = React.lazy(() => import("views/Help/FaqPage.js"))
const HitClubPage = React.lazy(() => import("views/HitClub/Pages/HitClubPage.js"))
const Logout = React.lazy(() => import("views/Logout/Logout.js"))
const Onboarding = React.lazy(() => import("views/Onboarding/Onboarding.js"))
const ErrorPage = React.lazy(() => import("views/ErrorPage/ErrorPage.js"))
const PaymentRefresh = React.lazy(() => import("views/Payments/PaymentRefresh.js"))
const PaymentReturn = React.lazy(() => import("views/Payments/PaymentReturn.js"))

//
// If we want to eject from lazily loading components
//

// import HitChatPage from "views/HitChat/Pages/HitChatPage.js"
// import HitManagerPage from "views/HitManager/Pages/HitManagerPage.js";
// import HitSearchPage from "views/HitSearch/HitSearchPage.js";
// import StudentProfilePage from "views/StudentProfile/StudentProfilePage.js";
// import HitBoardPage from "views/StudentProfile/HitBoardPage.js";
// import HitFactorCollectionPage from "views/CompanyProfile/HitFactorCollectionPage.js";
// import CompanyProfilePage from "views/CompanyProfile/CompanyProfilePage.js";
// import Authenticate from "views/Authenticate/Authenticate.js";
// import LoginPage from "views/LoginPage/LoginPage.js";
// import Logout from "views/Logout/Logout.js";
// import Onboarding from "views/Onboarding/Onboarding.js"
// import ErrorPage from "views/ErrorPage/ErrorPage.js"


// Error handling
import * as Sentry from "@sentry/react";


const App = () => {

    const dispatch = useDispatch();
    const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

    // States
    const authentication = useSelector(state => state.authentication)
    const metadata = useSelector(state => state.metadata.list)
    const selectedHitClub = useSelector(state => state.appSettings.selectedHitClub)
    const hobbies = useSelector(state => state.metadata.hobbies)

    const {
        userIsLoggedIn,
        userType
    } = authentication || {
        userIsLoggedIn: false,
        userType: ''
    }

    // States
    // const authentication = useSelector(state => state.authentication)
    // const companyId = useSelector(state => state?.currentUser?.instance?.company?.companyId)
    // const currentUserLanguage = useSelector(state => state?.currentUser?.instance?.language)

    // const {
    //     userIsLoggedIn,
    //     userType
    // } = authentication || {
    //     userIsLoggedIn: false,
    //     userType: ''
    // }

    // const { i18n } = useTranslation();

    // // Functions
    function pollNotifications(time) {
        useInterval(() => {
            store.dispatch(Notifications.onPollRequest());
        }, time);
    }    

    // // Get default values from backend 
    // useEffect(() => {
    //     store.dispatch(Metadata.onGetRequest());
    //     store.dispatch(Notifications.onPollRequest()); // So we poll notifications if page reloaded
    //     if(currentUserLanguage !== undefined) {
    //         i18n.changeLanguage(currentUserLanguage);
    //     }
    // }, []);

    // Poll for new notifications every five minutes
    pollNotifications(300000);
    // pollNotifications(1000);

    // useEffect(() => {
    //     if(!metadata?.length) {
    //         dispatch(Metadata.onGetRequest());
    //     }
    // }, [metadata])

    useEffect(() => {
        if(userIsLoggedIn) {
            dispatch(Notifications.onPollRequest()); // So we poll notifications if page reloaded
            if(userType !== 'COMPANY_USER') {
                dispatch(HitClub.onGetHitClubOwnerRequest({
                    hitClubId: selectedHitClub
                }));
            }
        }

        // if([ 'STUDENT', 'HITCLUB_ADMIN' ].includes(userType)) {
        //     if(hobbies?.length === 0) {
        //         dispatch(Metadata.onGetHobbiesRequest());
        //     }
        // }

    }, [])
    
    return (
        <BrowserRouter>
                <Suspense fallback={<LoadingScreen />}>      
                    <Sentry.ErrorBoundary
                        fallback={({ error, componentStack, eventId, resetError }) => ErrorFallback({error, componentStack, eventId, resetError})}
                        onReset={() => {
                        // reset the state of your app so the error doesn't happen again
                        }}
                    >  
                        <SentryRoutes>
                            {/* AUTHENTICATED ROUTES */}
                            <Route 
                                path="/student/:userId/hitboard/:hitBoardId" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER' ]}><HitBoardPage /></RouteLoggedIn>} 
                            />
                            <Route 
                                path="/student/:userId" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER' ]}><StudentProfilePage /></RouteLoggedIn>} 
                            />
                            <Route  
                                path="/foretag/:companyId/hitfactorcollection/:hitFactorCollectionId" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER' ]}><HitFactorCollectionPage /></RouteLoggedIn>} 
                            />
                            <Route 
                                path="/foretag/:userId" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER' ]}><CompanyProfilePage /></RouteLoggedIn>} 
                            />
                            {/* <Route  
                                path="/hitsearch" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER' ]}><HitSearchPage /></RouteLoggedIn>} 
                            /> */}
                            <Route 
                                path="/hitmanager/*" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER' ]}><HitManagerPage /></RouteLoggedIn>} 
                            />
                            <Route 
                                path="/hitchat/*" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER', 'STUDENT', 'HITCLUB_ADMIN' ]}><HitChatPage /></RouteLoggedIn>} 
                            />
                            <Route 
                                path="/installningar" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER', 'STUDENT', 'HITCLUB_ADMIN' ]}><SettingsPage /></RouteLoggedIn>} 
                            />
                            <Route 
                                path="/hjalp" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER' ]}><FaqPage /></RouteLoggedIn>} 
                            />
                            <Route 
                                path="/skapa-profil" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER' ]}><Onboarding /></RouteLoggedIn>} 
                            />

                            <Route 
                                path="/hitclub/*" 
                                element={<RouteLoggedIn roles={[ 'STUDENT', 'HITCLUB_ADMIN' ]}><HitClubPage /></RouteLoggedIn>} 
                            />

                            <Route 
                                path="/betalningar/refresh" 
                                element={<RouteLoggedIn roles={[ 'STUDENT', 'HITCLUB_ADMIN']}><PaymentRefresh /></RouteLoggedIn>} 
                            />
                            <Route 
                                path="/betalningar/return" 
                                element={<RouteLoggedIn roles={[ 'STUDENT', 'HITCLUB_ADMIN' ]}><PaymentReturn /></RouteLoggedIn>} 
                            />

                            <Route 
                                path="/loggaut" 
                                element={<RouteLoggedIn roles={[ 'COMPANY_USER', 'STUDENT', 'HITCLUB_ADMIN' ]}><Logout /></RouteLoggedIn>} 
                            />

                            {/* NOT AUTHENTICATED ROUTES */}
                            <Route path="/autentisera" element={<Authenticate />} />
                            <Route path="/loggain" element={<LoginPage />} />
                            <Route path="/*" element={<Authenticate />} />
                            <Route path="*" element={<ErrorPage />} />

                        </SentryRoutes>
                    </Sentry.ErrorBoundary>
                </Suspense>
        </BrowserRouter>
    );
};

function ErrorFallback({error, componentStack, eventId, resetError}) {
    return (
        <ErrorPage 
            error={error}
            componentStack={componentStack}
            eventId={eventId}
            resetError={resetError}
        />
    )
  }

ErrorFallback.propTypes = {
    error: PropTypes.string,
    resetError: PropTypes.func,
    eventId: PropTypes.string,
    componentStack: PropTypes.element
};

export default App;


  
